/*
 * <oilogic-floating-collage> — cursor-repel layer for collage floating items.
 * Each child .hero-banner-pages__floater is gently pushed away from the mouse
 * pointer (max ~1.6px per depth unit, distance falloff, lerp smoothing) and
 * glides back to its base position when the cursor leaves the section.
 * Progressive enhancement: without JS the items stay static (CSS handles the
 * idle float animation). Mouse pointers only; skipped entirely under
 * prefers-reduced-motion.
 */
(() => {
  const REPEL_RADIUS = 180;
  const REPEL_PER_DEPTH = 1.6;
  const LERP = 0.1;
  const EPSILON = 0.1;

  class OilogicFloatingCollage extends HTMLElement {
    #items = [];
    #raf = 0;
    #pointer = null;
    #abort = null;

    connectedCallback() {
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;

      this.#items = [...this.querySelectorAll('.hero-banner-pages__floater')]
        .map((el) => {
          const depth = Number(el.dataset.depth);
          return {
            el,
            depth: Number.isFinite(depth) ? depth : 5,
            dx: 0,
            dy: 0,
            tx: 0,
            ty: 0,
          };
        })
        /* depth 0 = fully static item — never repelled. */
        .filter((item) => item.depth > 0);
      if (this.#items.length === 0) return;

      this.#abort = new AbortController();
      const { signal } = this.#abort;
      const zone = this.closest('.hero-banner-pages') || this;

      zone.addEventListener(
        'pointermove',
        (event) => {
          if (event.pointerType !== 'mouse') return;
          this.#pointer = { x: event.clientX, y: event.clientY };
          this.#start();
        },
        { signal }
      );

      zone.addEventListener(
        'pointerleave',
        () => {
          this.#pointer = null;
          this.#start();
        },
        { signal }
      );
    }

    disconnectedCallback() {
      this.#abort?.abort();
      cancelAnimationFrame(this.#raf);
      this.#raf = 0;
    }

    #start() {
      if (!this.#raf) this.#raf = requestAnimationFrame(() => this.#tick());
    }

    #tick() {
      this.#raf = 0;
      let settling = false;

      for (const item of this.#items) {
        if (this.#pointer) {
          /* Base center = current rect minus the displacement already applied
             (the idle float lives on the inner <img>, so it never skews this). */
          const rect = item.el.getBoundingClientRect();
          const cx = rect.left + rect.width / 2 - item.dx;
          const cy = rect.top + rect.height / 2 - item.dy;
          const vx = cx - this.#pointer.x;
          const vy = cy - this.#pointer.y;
          const distance = Math.hypot(vx, vy) || 1;
          const falloff = Math.max(0, 1 - distance / REPEL_RADIUS);
          const strength = REPEL_PER_DEPTH * item.depth * falloff;
          item.tx = (vx / distance) * strength;
          item.ty = (vy / distance) * strength;
        } else {
          item.tx = 0;
          item.ty = 0;
        }

        item.dx += (item.tx - item.dx) * LERP;
        item.dy += (item.ty - item.dy) * LERP;

        if (Math.abs(item.tx - item.dx) > EPSILON || Math.abs(item.ty - item.dy) > EPSILON) {
          settling = true;
          item.el.style.transform = `translate3d(${item.dx}px, ${item.dy}px, 0)`;
        } else {
          item.dx = item.tx;
          item.dy = item.ty;
          item.el.style.transform = `translate3d(${item.dx}px, ${item.dy}px, 0)`;
        }
      }

      /* Keep animating only while something is still gliding — a parked cursor
         costs zero frames; the next pointermove restarts the loop. */
      if (settling) this.#start();
    }
  }

  if (!customElements.get('oilogic-floating-collage')) {
    customElements.define('oilogic-floating-collage', OilogicFloatingCollage);
  }
})();
